import React, { useState } from 'react';
import DataTable from '../../components/Datatable';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Circle, Calendar, Clock, AlertCircle, CircleCheck } from 'lucide-react';

const CategoryModal = ({ categories, isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/50"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
            className="overflow-hidden relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl"
          >
            {/* Gradient Bar */}
            <motion.div 
              className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 to-purple-500"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.2 }}
            />

            {/* Header */}
            <div className="flex justify-between items-center pb-4 mb-6 border-b border-gray-100">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className="text-xl font-semibold text-gray-800">Categories</h3>
                <p className="mt-1 text-sm text-gray-500">All available categories for this deal</p>
              </motion.div>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-gray-400 rounded-full hover:text-gray-600 hover:bg-gray-100"
                onClick={onClose}
              >
                <X size={20} />
              </motion.button>
            </div>

            {/* Categories List */}
            <div className="max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
              <motion.ul className="space-y-2">
                {categories.map((category, index) => (
                  <motion.li
                    key={category}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.4 }}
                    className="flex items-center px-4 py-3 text-gray-700 bg-gray-50 rounded-xl cursor-pointer hover:bg-blue-50 group"
                  >
                    <Circle size={6} className="mr-3 text-blue-500 fill-current" />
                    <span className="flex-grow font-medium">{category}</span>
                    <ChevronRight size={18} className="text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
                  </motion.li>
                ))}
              </motion.ul>
            </div>

            {/* Footer */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex justify-end pt-4 mt-6 border-t border-gray-100"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                onClick={onClose}
              >
                Close
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CategoryCell = ({ value }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (Array.isArray(value)) {
    if (value.length <= 1) {
      return (
        <motion.span
          whileHover={{ scale: 1.05 }}
          className="px-2.5 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full"
        >
          {value[0] || 'N/A'}
        </motion.span>
      );
    } else {
      return (
        <>
          <div className="flex gap-2 items-center">
            <motion.span
              whileHover={{ scale: 1.05 }}
              className="px-2.5 py-1 bg-gray-100 text-gray-800 text-[11px] font-medium rounded-full"
            >
              {value[0]}
            </motion.span>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsModalOpen(true)}
              className="flex justify-center items-center w-6 h-6 text-[11px] font-medium text-white bg-blue-500 rounded-full shadow-sm hover:bg-blue-600"
              title="View all categories"
            >
              +{value.length - 1}
            </motion.button>
          </div>
          <CategoryModal
            categories={value}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        </>
      );
    }
  }
  
  return (
    <motion.span
      whileHover={{ scale: 1.05 }}
      className="px-2.5 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full"
    >
      {value || 'N/A'}
    </motion.span>
  );
};

const DateCell = ({ value, type }) => {
  // Handle null or undefined values
  if (!value) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative group"
      >
        <div className="flex gap-2 items-center px-3 py-2 rounded-lg transition-colors hover:bg-gray-50">
          {type === 'created' ? (
            <Clock size={16} className="text-blue-500" />
          ) : (
            <Calendar size={16} className="text-gray-400" />
          )}
          <span className="text-[11px] font-medium text-gray-500">N/A</span>
        </div>
      </motion.div>
    );
  }

  const date = new Date(value.split('/').reverse().join('-'));
  const today = new Date();
  
  const diffTime = date - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date);
  
  const timeAgo = () => {
    const seconds = Math.floor((today - date) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (let [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval === 1 ? '' : 's'} ago`;
      }
    }
    return 'Just now';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative group"
    >
      <div className="flex gap-2 items-center px-3 py-2 rounded-lg transition-colors hover:bg-gray-50">
        {type === 'created' ? (
          <Clock size={16} className="text-blue-500" />
        ) : (
          <Calendar size={16} className={`
            ${diffDays < 0 ? 'text-red-500' : diffDays <= 7 ? 'text-orange-500' : 'text-green-500'}
          `} />
        )}
        <span className="text-[11px] font-medium text-gray-700">{formattedDate}</span>
      </div>

      {/* Tooltip */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileHover={{ opacity: 1, scale: 1 }}
        className="absolute z-10 invisible group-hover:visible bg-white border border-gray-100 rounded-xl shadow-lg p-3 min-w-[200px] -top-2 left-1/2 transform -translate-x-1/2 -translate-y-full"
      >
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-500">
              {type === 'created' ? 'Created' : 'Expires'}
            </span>
            {type === 'expiry' && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                diffDays < 0
                  ? 'bg-red-100 text-red-700'
                  : diffDays <= 7
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                {diffDays < 0
                  ? 'Expired'
                  : diffDays === 0
                  ? 'Today'
                  : `${diffDays} days left`}
              </span>
            )}
          </div>
          
          <div className="text-sm font-medium text-gray-700">
            {formattedDate}
          </div>
          
          <div className="text-xs text-gray-500">
            {type === 'created' ? (
              timeAgo()
            ) : (
              <div className="flex gap-1 items-center">
                <AlertCircle size={14} className={
                  diffDays < 0 ? 'text-red-500' : diffDays <= 7 ? 'text-orange-500' : 'text-green-500'
                } />
                {diffDays < 0
                  ? `Expired ${Math.abs(diffDays)} days ago`
                  : diffDays === 0
                  ? 'Expires today'
                  : `Expires in ${diffDays} days`}
              </div>
            )}
          </div>
        </div>
        
        {/* Tooltip arrow */}
        <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-white border-r border-b border-gray-100 transform rotate-45 -translate-x-1/2 translate-y-1/2"></div>
      </motion.div>
    </motion.div>
  );
};

const StatusCell = ({ value, expiryDate }) => {
  // Handle null or undefined expiryDate
  if (!expiryDate) {
    // If no expiry date, just show the regular status
    const statusConfig = {
      approved: {
        className: 'bg-green-50 text-green-700 border border-green-200',
        icon: <Circle size={12} className="text-green-500 fill-current" />
      },
      draft: {
        className: 'bg-gray-50 text-gray-700 border border-gray-200',
        icon: <Circle size={12} className="text-gray-500" />
      },
      pending: {
        className: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
        icon: <Circle size={12} className="text-yellow-500" />
      },
      rejected: {
        className: 'bg-red-50 text-red-700 border border-red-200',
        icon: <Circle size={12} className="text-red-500" />
      }
    };

    const status = value?.toLowerCase() || 'draft';
    const config = statusConfig[status] || statusConfig.draft;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex gap-2 items-center"
      >
        <span className={`flex items-center px-3 py-1 text-xs font-medium rounded-full border ${config.className}`}>
          {config.icon}
          <span className="ml-1 capitalize">{status}</span>
        </span>
      </motion.div>
    );
  }

  // Check if deal is expired
  const date = new Date(expiryDate.split('/').reverse().join('-'));
  const today = new Date();
  const isExpired = date < today;

  // If expired, override the status
  if (isExpired) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex gap-2 items-center"
      >
        <span className="flex relative items-center px-3 py-1 text-xs font-medium text-red-700 bg-red-50 rounded-full">
          <span className="flex absolute -left-1 w-2 h-2">
            <span className="inline-flex absolute w-full h-full bg-red-400 rounded-full opacity-75 animate-ping"></span>
            <span className="inline-flex relative w-2 h-2 bg-red-500 rounded-full"></span>
          </span>
          <span className="pl-2">Expired</span>
        </span>
      </motion.div>
    );
  }

  // Regular status styles
  const statusConfig = {
    approved: {
      className: 'bg-green-50 text-green-700 border border-green-200',
      icon: <Circle size={12} className="text-green-500 fill-current" />
    },
    draft: {
      className: 'bg-gray-50 text-gray-700 border border-gray-200',
      icon: <Circle size={12} className="text-gray-500" />
    },
    pending: {
      className: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
      icon: <Circle size={12} className="text-yellow-500" />
    },
    rejected: {
      className: 'bg-red-50 text-red-700 border border-red-200',
      icon: <Circle size={12} className="text-red-500" />
    }
  };

  const status = value.toLowerCase();
  const config = statusConfig[status] || statusConfig.draft;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      className="flex gap-2 items-center"
    >
      <span className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full ${config.className}`}>
        {config.icon}
        <span>{value}</span>
      </span>
    </motion.div>
  );
};

const columns = [
  { key: 'image', label: 'Image',
    sortable: false,
    render: (value) => {
      return <img src={value} alt="Deal" className="w-10 h-10 rounded-full" />;
    },
  },
  { key: 'title', label: 'Deal Title', sortable: true },
  { 
    key: 'category', 
    label: 'Category',
    sortable: true,
    render: (value) => <CategoryCell value={value} />
  },
  { 
    key: 'start_date',
    label: 'Date Created',
    sortable: true,
    render: (value) => <DateCell value={value} type="created" />
  },
  { 
    key: 'expiry_date',
    label: 'Expiry Date',
    sortable: true,
    render: (value) => <DateCell value={value} type="expiry" />
  },
  {
    key: 'status',
    label: 'Deal Status',
    sortable: true,
    render: (value, rowData) => {
      // Get expiry_date from the original data object
      const expiryDate = rowData?.expiry_date;
      return <StatusCell value={value} expiryDate={expiryDate} />;
    }
  },
];

const RecentDeals = ({deals}) => {
  return (
    <motion.div
      className="p-6 bg-white rounded-lg shadow-md"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-semibold text-md">Deals Summary</h2>
      </div>
      
      <DataTable 
        columns={columns} 
        data={deals} 
        className="rounded-lg border border-gray-200"
      />
    </motion.div>
  );
};

export default RecentDeals;